import {
  AddOutlined,
  ContentCopy,
  DeleteOutlineOutlined,
  Edit,
  Power,
  PowerOff,
} from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { useCfgPerm, useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import ApplyAuthGroupsTableControl from "@/components/apply-auth-groups-table-control";
import BulkOperationReportModal from "@/components/bulk-operation-report-modal";
import Popconfirm from "@/components/popcofirm";
import { useTableSettings } from "@/hooks/use-table-settings";
import { assembleNamesFilter } from "@/lib/cfg-utils";
import { ROOM_CONFIGS_NAMES } from "@/lib/constants";
import {
  useConfigChangeEnabled,
  useDeleteConfigs,
  useSaveConfig,
  useUpdateConfig,
} from "@/module/room-configs-module/hooks/room-configs-hooks";
import {
  TimingCreateEditModal,
  TimingForm,
} from "@/module/room-configs-module/timing-config-module/timing-create-edit-modal";
import { ResultDtoLong, SysCfgRegularDto } from "@/shared/api";

const TimingConfigTable = ({ room }: { room: string }) => {
  const { t } = useTranslation();
  const {
    data: configsRs,
    isLoading: configsIsLoading,
    refetch,
  } = useSysCfgQuery({
    domain: room,
    config_name: ROOM_CONFIGS_NAMES.TIMING,
  });

  const cfgPerm = useCfgPerm({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.TIMING,
  });

  const isOwner = useIsOwnerOrSuperAdmin();

  const [bulkOperationResult, setBulkOperationResult] = useState<ResultDtoLong[] | undefined>(
    undefined
  );

  const { saveConfigMutation: handleClickSaveNewConfig, isPending: isSaveConfigPending } =
    useSaveConfig({
      roomName: room,
      cfgName: ROOM_CONFIGS_NAMES.TIMING,
      onSuccessCallback: () => {
        void refetch();
      },
    });

  const { updateConfigMutation: handleClickUpdateConfig, isPending: isUpdateConfigPending } =
    useUpdateConfig({
      roomName: room,
      cfgName: ROOM_CONFIGS_NAMES.TIMING,
      onSuccessCallback: () => {
        void refetch();
      },
    });

  const handleChangeEnableBulk = useConfigChangeEnabled({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.TIMING,
    onSuccessCallback: (table) => {
      void refetch();
      table.resetRowSelection();
    },
  });

  const handleDeleteConfigs = useDeleteConfigs({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.TIMING,
    onSuccessCallback: (table, response) => {
      setBulkOperationResult([response.data]);
      void refetch();
      table.resetRowSelection();
    },
  });

  const [modalMode, setModalMode] = useState<"edit" | "add" | undefined>(undefined);
  const [modalInitValues, setModalInitValues] = useState<SysCfgRegularDto | undefined>(
    undefined
  );
  const [editItemName, setEditItemName] = useState<string>("");

  function handleClickAddConfig() {
    setModalInitValues(undefined);
    setEditItemName("");
    setModalMode("add");
  }

  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<SysCfgRegularDto>("TIMING");

  const columns = useMemo<MRT_ColumnDef<SysCfgRegularDto /*SysCfgCmsAggregator*/>[]>(
    () => [
      {
        header: t("cmsAggregatorTable.enable"),
        filterFn: "contains",
        enableEditing: false,
        accessorFn: (row) => row.enabled ?? "-",
        size: 80,
        Cell: ({ row }) => <Checkbox checked={row.original.enabled} disabled />,
      },
      {
        header: t("cmsAggregatorTable.name"),
        accessorFn: (row) => row.name ?? "-",
        filterFn: "contains",
      },
    ],
    [t]
  );

  const table = useMaterialReactTable({
    data: configsRs?.data.content ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.id,
    selectAllMode: "all",
    state: {
      isLoading: configsIsLoading,
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    ...defaultProps,
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
      sorting: [{ id: "name", desc: false }],
    },
    enableRowActions: true,
    enableSelectAll: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: false,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enablePagination: true,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        verticalAlign: "middle",
        "& *": {
          whiteSpace: "normal",
          lineHeight: 1.35,
        },
      },
    },
    defaultColumn: {
      enableResizing: false,
      grow: false,
      maxSize: 30,
      minSize: 10,
    },

    renderTopToolbarCustomActions: ({ table }) => {
      const selectedCount = table.getSelectedRowModel().rows.length;

      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          {cfgPerm.write && (
            <Button onClick={() => handleClickAddConfig()}>
              <AddOutlined /> {t("new")}
            </Button>
          )}

          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}>
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disable")}
                </IconButton>
              )}

              {cfgPerm.delete && (
                <Popconfirm
                  title={t("deleteItems")}
                  description={t("areYouSureToDeleteItems")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain={room}
                  cfgName={ROOM_CONFIGS_NAMES.TIMING}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetch()}
                />
              )}
            </div>
          )}
        </div>
      );
    },
    renderRowActions: ({ row }) => (
      <Box>
        <IconButton
          onClick={() => {
            setModalInitValues({
              ...row.original,
              auth_groups: new Set(row.original.auth_groups),
            });
            setModalMode("edit");
            setEditItemName(row.original.name);
          }}
        >
          <Edit />
        </IconButton>

        {cfgPerm.write && (
          <IconButton
            onClick={() => {
              setModalInitValues({
                ...row.original,
                data: { ...row.original.data, comment: "" },
                name: "",
                enabled: false,
              });
              setModalMode("add");
            }}
          >
            <ContentCopy />
          </IconButton>
        )}
      </Box>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <>
      <MaterialReactTable table={table} />

      <TimingCreateEditModal
        open={!!modalMode}
        mode={modalMode}
        initialData={modalInitValues as TimingForm}
        onSave={(data) => {
          if (modalMode === "add") return handleClickSaveNewConfig(data);
          if (modalMode === "edit") return handleClickUpdateConfig(editItemName, data);
        }}
        onCancel={() => {
          setModalMode(undefined);
          setModalInitValues(undefined);
          setEditItemName("");
        }}
      />

      {bulkOperationResult && (
        <BulkOperationReportModal
          resultList={bulkOperationResult}
          title={t("operationResult")}
          showProblematic
          showSuccess
          onCancel={() => setBulkOperationResult(undefined)}
          open={!!bulkOperationResult}
        />
      )}
    </>
  );
};

export default TimingConfigTable;
